// PickupSelection — read/write the customer's pickup selection
// Doesn't change DOM/UI directly
// Source of truth = cart attributes
window.PickupSelection = new (class {
  #ATTR = {
    dateIso: '_pickup_date_iso',
    location: 'Pickup Location',
    handle: '_pickup_location_handle',
    scheduledAt: '_pickup_scheduled_at',
  };

  #emptyState() {
    return {
      handle: undefined,
      locationName: undefined,
      day: undefined,
    };
  }

  #decodeHtml(value) {
    if (!value) return undefined;

    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  // In-memory pickup selection state
  #state = this.#emptyState();

  // Set state from the cart (server-rendered in pickup-context.liquid)
  constructor() {
    const seed = (window.PickupContext && window.PickupContext.currentSelection) || {};
    this.#state.handle = seed.handle || undefined;
    this.#state.locationName = this.#decodeHtml(seed.locationName);
    this.#state.day = seed.day || undefined;
  }

  #writeCart(attrs) {
    const form = new FormData();
    Object.keys(attrs).forEach((key) => form.append(`attributes[${key}]`, attrs[key]));
    return fetch('/cart/update.js', {
      method: 'POST',
      body: form,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }).then(async (response) => {
      if (!response.ok) {
        const errorText = await response.text();
        console.error('PickupSelection cart update failed:', { status: response.status, response: errorText });
        throw new Error(`Cart update failed: ${response.status}`);
      }
      return response.json();
    });
  }

  // Notifies pickup-display.js (via the pickupSelectionChanged event) to re-render the UI
  #notifyPickupChanged() {
    window.dispatchEvent(new CustomEvent('pickupSelectionChanged', { detail: this.get() }));
  }

  get() {
    return { ...this.#state };
  }

  // A location needs both handle and name (reject rather than write 'default'); day-only sets the date.
  set({ handle, locationName, day } = {}) {
    const attrs = {};
    const prev = { ...this.#state };

    if (handle != null || locationName != null) {
      if (!handle || !locationName) {
        return Promise.reject(
          new Error(
            `PickupSelection.set: a location needs both handle and locationName (got handle=${handle}, locationName=${locationName})`
          )
        );
      }
      attrs[this.#ATTR.location] = locationName;
      attrs[this.#ATTR.handle] = handle;
      attrs[this.#ATTR.scheduledAt] = new Date().toISOString();
      this.#state.handle = handle;
      this.#state.locationName = locationName;

      // Changing to a different location invalidates any previously chosen day
      if (prev.handle && prev.handle !== handle) {
        attrs[this.#ATTR.dateIso] = '';
        this.#state.day = undefined;
      }
    }

    if (day != null) {
      if (!day) return Promise.reject(new Error('PickupSelection.set: empty day'));
      attrs[this.#ATTR.dateIso] = day;
      this.#state.day = day;
    }

    if (Object.keys(attrs).length === 0) return Promise.resolve(null);
    this.#notifyPickupChanged();
    return this.#writeCart(attrs).catch((err) => {
      this.#state = prev;
      this.#notifyPickupChanged();
      throw err;
    });
  }

  setLocation(location) {
    return this.set(location);
  }

  setDay(day) {
    return this.set({ day });
  }

  // Clear only the chosen day, keeping the location
  clearDay() {
    const prev = { ...this.#state };
    this.#state.day = undefined;
    this.#notifyPickupChanged();
    return this.#writeCart({ [this.#ATTR.dateIso]: '' }).catch((err) => {
      this.#state = prev;
      this.#notifyPickupChanged();
      throw err;
    });
  }

  clear() {
    const prev = { ...this.#state };
    this.#state = this.#emptyState();
    this.#notifyPickupChanged();
    return this.#writeCart({
      [this.#ATTR.dateIso]: '',
      [this.#ATTR.location]: '',
      [this.#ATTR.handle]: '',
      [this.#ATTR.scheduledAt]: '',
    }).catch((err) => {
      this.#state = prev;
      this.#notifyPickupChanged();
      throw err;
    });
  }
})();
